/* "New build / Ask the crew" — the single kick-off-new-work entry (v2
   repurposing of Start-a-build). One prompt → a tracked task in the dispatch
   queue (/api/tasks → D1 + Slack mirror) + an audit row. The same bridge the
   Talk dock uses (docs/DISPATCH_BRIDGE.md). */
import { useState } from 'react';
import { Button, Dialog } from '../../ds';
import { useOps } from '../ops';
import { Kicker } from '../ui';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function NewBuildDialog({ open, onClose }: Props) {
  const { pushToast, appendAudit } = useOps();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const task = text.trim();
    if (!task || busy) return;
    setBusy(true);
    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ op: 'create', text: task, from: 'Cassidy' }),
      });
      const body = (await res.json()) as { ok?: boolean; id?: string; persisted?: boolean };
      if (!res.ok || !body.ok) throw new Error('dispatch failed');
      appendAudit({ actor: 'Cassidy', action: 'Kicked off build', target: task.slice(0, 60) });
      pushToast(
        'success',
        'Briefed the crew',
        body.persisted ? `Task ${body.id} queued — Atlas will route it.` : 'Accepted (queue not bound in this environment).',
      );
      setText('');
      onClose();
    } catch {
      pushToast('danger', 'Dispatch failed', 'Couldn’t reach the task queue — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="New build — ask the crew"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Kicker>// lands in the crew queue + Slack</Kicker>
          <span style={{ display: 'flex', gap: 10 }}>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={() => void submit()} disabled={busy || !text.trim()}>
              {busy ? 'Dispatching…' : 'Dispatch to crew'}
            </Button>
          </span>
        </div>
      }
    >
      <p style={{ margin: '0 0 10px', color: 'var(--muted)', fontSize: 13.5, lineHeight: 1.55 }}>
        One clear ask — a feature, a fix, a brief. Atlas routes it to the right agent and it shows up as a tracked task.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void submit(); } }}
        placeholder="e.g. Add a /changelog page that lists merged PRs by week…"
        aria-label="Describe the work"
        rows={4}
        autoFocus
        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontFamily: 'var(--font-body)', fontSize: 13.5, outline: 'none' }}
      />
    </Dialog>
  );
}
